/**
 * "Why book with RentalCar.al?" section (Iter 67h).
 *
 * Replaces the older 3-card "Why thousands choose RentalCar.al" block on the home
 * page with a richer 5-benefit grid. Each card uses a distinct lucide icon and
 * its own colour palette — matches the Iter-67 visual identity (sky → indigo
 * → violet → amber → rose → emerald) so the home page feels like a continuation
 * of the booking funnel.
 *
 * A leading badge surfaces the strongest hook: "No deposit or credit card
 * required" — which is what most first-time renters are checking for.
 */
import { ShieldCheck, Tag, CalendarClock, Wallet, Headphones, BadgeCheck } from "lucide-react";

const BENEFITS = [
  {
    icon: ShieldCheck,
    title: "Low or no-deposit car rental",
    // Iter 67dv — keyword pass: "car rental", "low or no deposit",
    // "no credit card required".
    text: "Choose Full Protection to unlock low or no-deposit car rental on many cars — no credit card required. If a partner requires a deposit, the amount is shown clearly before pickup.",
    bg: "from-emerald-50 via-white to-teal-50/60",
    ring: "ring-emerald-100 border-emerald-100",
    iconBg: "bg-emerald-50",
    iconFg: "text-emerald-600",
    testId: "benefit-full-protection",
  },
  {
    icon: Tag,
    title: "Clear prices, no hidden fees",
    text: "Taxes, mandatory fees, and required insurance are shown upfront — book a car at Tirana Airport, Vlora, Saranda or anywhere in Albania and know exactly what you pay before you confirm.",
    bg: "from-sky-50 via-white to-indigo-50/60",
    ring: "ring-sky-100 border-sky-100",
    iconBg: "bg-sky-50",
    iconFg: "text-sky-600",
    testId: "benefit-clear-pricing",
  },
  {
    icon: CalendarClock,
    title: "Free cancellation up to 24h",
    text: "Plans changed? Most RentalCar.al reservations can be cancelled or modified free of charge up to 24 hours before pickup.",
    bg: "from-violet-50 via-white to-fuchsia-50/60",
    ring: "ring-violet-100 border-violet-100",
    iconBg: "bg-violet-50",
    iconFg: "text-violet-600",
    testId: "benefit-free-cancellation",
  },
  {
    icon: Wallet,
    title: "Reserve online, pay the rest at pickup",
    text: "Booking a car has never been easier — pay a small online service fee to secure your reservation, then settle the remaining rental amount directly with the rent-a-car partner at the counter.",
    bg: "from-amber-50 via-white to-orange-50/60",
    ring: "ring-amber-100 border-amber-100",
    iconBg: "bg-amber-50",
    iconFg: "text-amber-600",
    testId: "benefit-pay-at-pickup",
  },
  {
    icon: Headphones,
    title: "24/7 support across Albania",
    text: "Need help before, during, or after your car rental? Our support team is available whenever your trip needs attention — from Tirana Airport pickup to Saranda drop-off and beyond.",
    bg: "from-rose-50 via-white to-pink-50/60",
    ring: "ring-rose-100 border-rose-100",
    iconBg: "bg-rose-50",
    iconFg: "text-rose-600",
    testId: "benefit-support-24-7",
  },
];

export default function WhyRentalCar() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="why-rentalcar">
      <div className="max-w-3xl mb-12">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm mb-5">
          <BadgeCheck className="h-3 w-3" />
          No deposit · No credit card required
        </div>
        <h2 className="font-display text-3xl lg:text-5xl tracking-tight font-medium" data-testid="why-rentalcar-title">
          Why book your car rental with RentalCar.al?
        </h2>
        <p className="mt-4 text-neutral-600 text-lg leading-relaxed">
          Compare real car rental offers from verified rent-a-car partners across Albania — from Tirana to Saranda.
          Final price shown upfront — no surprises, low or no deposit, no credit card required, flexible cancellation, and support 24/7.
        </p>
        {/* Iter 67dv — semantic keyword line, lightly styled so it sits as a
            natural sub-statement (Google still crawls it; humans see a
            single-line context cue). Iter 67ej — Albania-only focus. */}
        <p className="mt-3 text-sm text-neutral-500 leading-relaxed" data-testid="why-keywords-line">
          Popular searches: <strong className="text-neutral-700">car rental Tirana Airport</strong> · <strong className="text-neutral-700">rent a car Albania</strong> · <strong className="text-neutral-700">low deposit car rental Saranda</strong> · <strong className="text-neutral-700">no credit card car hire Vlora</strong> · <strong className="text-neutral-700">car rental Durrës</strong> · <strong className="text-neutral-700">cheap car hire Ksamil</strong>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {BENEFITS.map((b, idx) => (
          <div
            key={b.title}
            className={`relative p-7 rounded-3xl border bg-gradient-to-br ${b.bg} ${b.ring} hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300`}
            style={{ animationDelay: `${idx * 80}ms` }}
            data-testid={b.testId}
          >
            <div className={`h-20 w-20 md:h-12 md:w-12 rounded-2xl ${b.iconBg} ring-1 ${b.ring} flex items-center justify-center mb-5 shadow-sm mx-auto md:mx-0`}>
              <b.icon className={`h-10 w-10 md:h-6 md:w-6 ${b.iconFg}`} aria-hidden="true" />
            </div>
            <div className="font-display text-xl font-medium mb-2 leading-snug text-center md:text-left">{b.title}</div>
            <div className="text-sm text-neutral-600 leading-relaxed text-center md:text-left">{b.text}</div>
          </div>
        ))}

        {/* Trailing decorative tile — a tiny "and more" pill so the 5-card grid
            doesn't leave an awkward empty cell on the lg:grid-cols-3 layout. */}
        <div className="hidden lg:flex p-7 rounded-3xl border border-dashed border-neutral-200 bg-neutral-50/40 items-center justify-center text-center">
          <div>
            <div className="font-display text-lg font-medium text-neutral-700 mb-1">
              And the rest.
            </div>
            <div className="text-sm text-neutral-500 leading-relaxed">
              Verified rent-a-car partners, multi-currency pricing, digital agreements, and PDF invoices on every car rental.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
